import React from 'react';

import { Stack } from 'react-bootstrap';
import ReactImageFallback from 'react-image-fallback';

import { LomavisShortenedNumber } from '../../utils/numbers/LomavisShortenedNumber';
import { LomavisOverlayToolTip1 } from '../../overlays/lomavisOverlayToolTip1/lomavisOverlayToolTip1';

/** The text to display inside the H1 heading */

export type ListItem = {
  image: string;
  title: string;
  subtitle?: string;
  value: string | number;
  highlight?: boolean;
};

interface LomavisList1Props {
  /** The text to display inside the H1 heading */

  placeholder: any /** fallback image */;

  data: ListItem[];

  showTooltip?: boolean;
}

/**
 * Atoms: LomavisList1
 */

type ParsedNumber = {
  number: number;
  unit?: string;
};

function parseValue(input: string | number): ParsedNumber {
  if (typeof input === 'number') {
    return { number: input };
  }

  // Trim spaces
  const trimmed = input?.trim();

  // Match numeric part (including commas, decimal, sign) and any unit after
  const match = trimmed?.match(/^([-+]?[0-9,]*\.?[0-9]+)\s*(.*)$/);

  if (!match) {
    return { number: 0 };
  }

  const rawNumber = match[1].replace(/,/g, ''); // remove commas
  const number = parseFloat(rawNumber) || 0;

  const unit = match[2] ? match[2].trim() : undefined;

  return { number, unit };
}
type ListCardProps = {
  image?: string;
  title: string;
  subtitle: string;
  value: string | number;
  highlight: boolean;
  fallback?: string;
  showTooltip?: boolean;
};

const ListCard = ({
  image,
  title,
  subtitle = '',
  value,
  highlight = false,
  fallback,
  showTooltip = false
}: ListCardProps) => {
  const { number, unit } = parseValue(value);

  return (
    <div
      className={`w-100 d-flex p-3 justify-content-between align-items-center ${
        highlight && 'bg-light-primary'
      }`}
    >
      <div className="d-flex align-items-center gap-2">
        {image && (
          <div className="me-3">
            <ReactImageFallback
              src={image}
              fallbackImage={fallback}
              initialImage={fallback}
              alt={title}
              className="w-50px h-50px rounded-circle"
            />
          </div>
        )}

        <div className="d-flex flex-column">
          <span className="text-gray-800 fw-bold text-hover-primary mb-1 fs-6">
            {title}
          </span>
          <span className="text-gray-500 fw-semibold d-block fs-7">
            {subtitle}
          </span>
        </div>
      </div>

      <span className=" fw-bold fs-6">
        {showTooltip ? (
          <LomavisOverlayToolTip1 fromCard={true} text={`${number}`}>
            {number ? (
              <span className="cursor-pointer">
                <LomavisShortenedNumber
                  value={number}
                  dataUnit={unit}
                  compactLength={3}
                />
              </span>
            ) : (
              '-'
            )}
          </LomavisOverlayToolTip1>
        ) : (
          <span>
            {number ? (
              <LomavisShortenedNumber
                value={number}
                dataUnit={unit}
                compactLength={3}
              />
            ) : (
              '-'
            )}
          </span>
        )}
      </span>
    </div>
  );
};

/**
 * A descending order list relative to the value.
 * We can render the data with or without image.
 * We abbrevate values depending on the value type we get.
 */

export const LomavisList1: React.FC<LomavisList1Props> = ({
  placeholder,
  data,
  showTooltip
}) => {
  function sortDataDescending<T extends { value: string | number }>(
    data: T[]
  ): T[] {
    return [...data].sort((a, b) => {
      const numA = parseValue(a.value).number;
      const numB = parseValue(b.value).number;
      return numB - numA; // highest first
    });
  }

  const processedData = sortDataDescending([...data]);

  return (
    <Stack
      className="lomavis-list1"
      style={{ maxHeight: '500px', overflowY: 'auto' }}
    >
      {processedData.map((item, index) => (
        <ListCard
          key={index}
          image={item.image}
          title={item.title}
          subtitle={item.subtitle}
          value={item.value}
          highlight={item.highlight}
          fallback={placeholder}
          showTooltip={showTooltip}
        />
      ))}
    </Stack>
  );
};
